import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Filter, Zap, Copy, BarChart3, Star, Users, MessageSquare, Mail, Phone, Share2, TrendingUp } from 'lucide-react';

import TemplateEditor from './components/TemplateEditor';
import AIGenerationPanel from './components/AIGenerationPanel';
import TemplateLibrary from './components/TemplateLibrary';
import TemplatePreview from './components/TemplatePreview';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import EmailTemplateGenerator from './components/EmailTemplateGenerator';
import { templateService } from '../../services/templateService';
import PageLayout from '../../components/ui/PageLayout';

const AITemplateStudio = () => {
  // Main state
  const [activeTab, setActiveTab] = useState('library');
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showAIOnly, setShowAIOnly] = useState(false);

  // Modal and selection state
  const [showEditor, setShowEditor] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEmailGenerator, setShowEmailGenerator] = useState(false);

  // Performance stats
  const [stats, setStats] = useState({
    totalTemplates: 0,
    aiGenerated: 0,
    totalUsage: 0,
    avgPerformance: 0
  });

  useEffect(() => {
    loadData();
  }, [selectedCategory, selectedType, showAIOnly, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        type: selectedType !== 'all' ? selectedType : undefined,
        aiGenerated: showAIOnly ? true : undefined,
        search: searchQuery || undefined
      };

      const [templatesData, categoriesData] = await Promise.all([
        templateService?.getTemplates?.(filters) || [],
        templateService?.getTemplateCategories?.() || [{ key: 'all', label: 'All Categories', count: 0 }]
      ]);

      setTemplates(templatesData || []);
      setCategories(categoriesData || []);

      // Calculate stats
      const totalUsage = templatesData?.reduce?.((sum, template) => 
        sum + (template?.template_analytics?.[0]?.usage_count || 0), 0
      ) || 0;
      
      const aiGenerated = templatesData?.filter?.(t => t?.ai_generated)?.length || 0;

      setStats({
        totalTemplates: templatesData?.length || 0,
        aiGenerated,
        totalUsage,
        avgPerformance: templatesData?.length > 0 ? Math.round((totalUsage / templatesData?.length) * 10) / 10 : 0
      });

    } catch (err) {
      console.error('Error loading template data:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    try {
      setSelectedTemplate(null);
      setShowEditor(true);
    } catch (err) {
      console.error('Error opening template editor:', err);
      setError('Failed to open template editor');
    }
  };

  const handleEditTemplate = (template) => {
    try {
      setSelectedTemplate(template);
      setShowEditor(true);
    } catch (err) {
      console.error('Error editing template:', err);
      setError('Failed to edit template');
    }
  };

  const handlePreviewTemplate = (template) => {
    try {
      setSelectedTemplate(template);
      setShowPreview(true);
    } catch (err) {
      console.error('Error previewing template:', err);
      setError('Failed to preview template');
    }
  };

  const handleAnalyzeTemplate = (template) => {
    try {
      setSelectedTemplate(template);
      setShowAnalytics(true);
    } catch (err) {
      console.error('Error analyzing template:', err);
      setError('Failed to analyze template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await templateService?.duplicateTemplate?.(template?.id, `${template?.name} (Copy)`);
      loadData(); // Refresh data
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError('Failed to duplicate template.');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await templateService?.deleteTemplate?.(templateId);
      loadData(); // Refresh data
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template.');
    }
  };

  const handleAIGenerate = () => {
    try {
      setShowAIPanel(true);
    } catch (err) {
      console.error('Error opening AI panel:', err);
      setError('Failed to open AI generation panel');
    }
  };

  const handleEmailAIGenerate = () => {
    setShowEmailGenerator(true);
  };

  const handleTemplateCreated = () => {
    try {
      setShowEditor(false);
      setShowAIPanel(false);
      loadData();
    } catch (err) {
      console.error('Error handling template creation:', err);
      setError('Failed to save template');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'call': return <Phone className="h-4 w-4" />;
      case 'social_media': return <Share2 className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const tabs = [
    { id: 'library', label: 'Template Library', icon: <Star className="h-4 w-4" /> },
    { id: 'create', label: 'Create New', icon: <Plus className="h-4 w-4" /> },
    { id: 'analytics', label: 'Performance', icon: <BarChart3 className="h-4 w-4" /> }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Template Studio</h1>
          <p className="text-muted-foreground">
            Create, customize, and optimize templates with AI-powered assistance
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleEmailAIGenerate}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email AI
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAIGenerate}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
          >
            <Zap className="h-4 w-4 mr-2" />
            Generate with AI
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCreateTemplate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </motion.button>
        </div>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquare className="h-6 w-6 text-blue-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats?.totalTemplates}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Total Templates</h3>
          <p className="text-xs text-gray-500">Active templates in library</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Zap className="h-6 w-6 text-purple-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats?.aiGenerated}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">AI Generated</h3>
          <p className="text-xs text-gray-500">Powered by artificial intelligence</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats?.totalUsage}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Total Usage</h3>
          <p className="text-xs text-gray-500">Times templates were used</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">{stats?.avgPerformance}</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Avg Performance</h3>
          <p className="text-xs text-gray-500">Usage per template</p>
        </motion.div>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="min-w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {categories?.map((category) => (
                <option key={category?.key} value={category?.key}>
                  {category?.label} ({category?.count})
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="min-w-40">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e?.target?.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Types</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="call">Call Script</option>
              <option value="social_media">Social Media</option>
            </select>
          </div>

          {/* AI Filter Toggle */}
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={showAIOnly}
                onChange={(e) => setShowAIOnly(e?.target?.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">AI Generated Only</span>
            </label>
          </div>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6"
        >
          {error}
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </motion.div>
      )}
      {/* Templates Grid */}
      <TemplateLibrary
        templates={templates}
        loading={loading}
        onPreview={handlePreviewTemplate}
        onEdit={handleEditTemplate}
        onDuplicate={handleDuplicateTemplate}
        onDelete={handleDeleteTemplate}
        onAnalyze={handleAnalyzeTemplate}
        getTypeIcon={getTypeIcon}
      />
      {/* Modals */}
      <AnimatePresence>
        {showEditor && (
          <TemplateEditor
            template={selectedTemplate}
            onClose={() => setShowEditor(false)}
            onSave={handleTemplateCreated}
          />
        )}

        {showEmailGenerator && (
          <EmailTemplateGenerator
            onClose={() => setShowEmailGenerator(false)}
            onSaved={() => {
              setShowEmailGenerator(false);
              loadData();
            }}
          />
        )}

        {showAIPanel && (
          <AIGenerationPanel
            onClose={() => setShowAIPanel(false)}
            onGenerated={handleTemplateCreated}
          />
        )}

        {showPreview && selectedTemplate && (
          <TemplatePreview
            template={selectedTemplate}
            onClose={() => setShowPreview(false)}
          />
        )}

        {showAnalytics && selectedTemplate && (
          <PerformanceAnalytics
            template={selectedTemplate}
            onClose={() => setShowAnalytics(false)}
          />
        )}
      </AnimatePresence>
    </PageLayout>
  );
};

export default AITemplateStudio;